/**
 * AlertMenu Component
 * 
 * A dropdown menu component for alert management that appears in the header.
 * Provides options for creating new alerts and managing existing ones.
 * 
 * Features:
 * - Dropdown with "Add Alert" and "Manage Alerts" options
 * - Proper styling with theme support
 * - Keyboard navigation
 * - Mobile responsiveness
 * 
 * @module AlertMenu
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * AlertMenu component for alert management options
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onAddAlert - Function to call when "Add Alert" is clicked
 * @param {Function} props.onManageAlerts - Function to call when "Manage Alerts" is clicked
 * @returns {JSX.Element} AlertMenu component
 */
function AlertMenu({ disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  /**
   * Toggle the dropdown menu
   */
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Close the dropdown menu
   */
  const closeMenu = () => {
    setIsOpen(false);
  };

  /**
   * Handle menu item click
   * 
   * @param {Function} handler - The handler function to call
   */
  const handleMenuItemClick = (path) => {
    if (path) {
      navigate(path);
    }
    closeMenu();
  };

  /**
   * Handle keyboard navigation
   * 
   * @param {KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = (e) => {
    if (isOpen) {
      // Close on Escape
      if (e.key === 'Escape') {
        closeMenu();
        buttonRef.current?.focus();
      }
      
      // Navigate with arrow keys
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const menuItems = menuRef.current?.querySelectorAll('.alert-menu-item');
        if (!menuItems || menuItems.length === 0) return;
        
        const currentIndex = Array.from(menuItems).findIndex(item => item === document.activeElement);
        let nextIndex;
        
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
        }
        
        menuItems[nextIndex].focus();
      }
    } else {
      // Open on arrow down when button is focused
      if ((e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter') && e.target === buttonRef.current) {
        e.preventDefault();
        setIsOpen(true);
      }
    }
  };

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus the first menu item when opening
  useEffect(() => {
    if (isOpen) {
      const menuItems = menuRef.current?.querySelectorAll('.alert-menu-item');
      if (menuItems && menuItems.length > 0) {
        setTimeout(() => {
          menuItems[0].focus();
        }, 10);
      }
    }
  }, [isOpen]);

  return (
    <div className="alert-menu-container" onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        className="alert-menu-button"
        onClick={disabled ? undefined : toggleMenu}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Alert menu"
        disabled={disabled}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
      >
        <span className="alert-menu-text">Alerts</span>
        <i className={`alert-menu-icon ${isOpen ? 'open' : ''}`} aria-hidden="true"></i>
      </button>
      
      {isOpen && (
        <div 
          ref={menuRef}
          className="alert-menu-dropdown"
          role="menu"
          aria-label="Alert options"
        >
          <button
            className="alert-menu-item"
            onClick={disabled ? undefined : () => handleMenuItemClick('/create-alert')}
            role="menuitem"
            tabIndex={disabled ? -1 : 0}
            disabled={disabled}
            aria-disabled={disabled}
            style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          >
            <i className="add-icon" aria-hidden="true"></i>
            <span>Add Alert</span>
          </button>
          <button
            className="alert-menu-item"
            onClick={disabled ? undefined : () => handleMenuItemClick('/manage-alerts')}
            role="menuitem"
            tabIndex={disabled ? -1 : 0}
            disabled={disabled}
            aria-disabled={disabled}
            style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          >
            <i className="manage-icon" aria-hidden="true"></i>
            <span>Manage Alerts</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default AlertMenu;
